import React from "react";
import "./style.css";
import { Form } from "react-bootstrap";
import { useState } from "react";
import { handleAlert } from "@utils/helperFunctions";
import useFlags from "@common/hooks/useFlags";
import { useGetProjectById } from "../ProjectServicesV2/Show/api";
import { useEffect } from "react";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import store from "../../store";
import {
  useV2UpdateProjectMutation,
  useV2CreateExecutiveSummaryRequestMutation,
  useV2GetAccountQuery,
  generatedV2,
} from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagicWandSparkles } from "@fortawesome/pro-solid-svg-icons";
import { AdminFormPage } from "@components/CatalogIndex/AdminFormPage";

const FORM_ID = "customer-summary-form";

interface Fields {
  executiveSummary: string;
  solutionSummary: string;
  ourResponsibilities: string;
  customerResponsibilities: string;
  outOfScope: string;
}

const DEFAULT_LABELS: Record<keyof Fields, string> = {
  executiveSummary: "Executive Summary",
  solutionSummary: "Solution Summary",
  ourResponsibilities: "Our Responsibilities",
  customerResponsibilities: "Customer Responsibilities",
  outOfScope: "Out of Scope",
};

const LABEL_KEYS: Record<keyof Fields, string> = {
  executiveSummary: "executive_summary",
  solutionSummary: "solution_summary",
  ourResponsibilities: "our_responsibilities",
  customerResponsibilities: "client_responsibilities",
  outOfScope: "out_of_scope",
};

const CustomerSummaryV2 = (props) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateProject] = useV2UpdateProjectMutation();
  const [createSummaryRequest] = useV2CreateExecutiveSummaryRequestMutation();
  const id = props.project_id;
  const { project, isLoading, isFetching } = useGetProjectById(id, {
    refetchOnMountOrArgChange: true,
  });
  const { configCustomerSummaryState } = useFlags();
  const { hasPrivilege } = usePrivilegeCheck();
  const { data: account } = useV2GetAccountQuery(
    {},
    { skip: !accountSlug }
  );
  const fieldLabels = (account?.data?.attributes as any)?.["field-labels"];
  const labelFor = (key: keyof Fields) =>
    fieldLabels?.[LABEL_KEYS[key]] || DEFAULT_LABELS[key];
  const [fields, setFields] = useState<Fields>({
    executiveSummary: "",
    solutionSummary: "",
    ourResponsibilities: "",
    customerResponsibilities: "",
    outOfScope: "",
  });
  const [responseMessage, setResponseMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFail, setShowFail] = useState(false);
  const [showSummaryRequestAlert, setShowSummaryRequestAlert] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isLoading && !isFetching && project) {
      setFields({
        executiveSummary: project.executiveSummary || "",
        solutionSummary: project.solutionSummary || "",
        ourResponsibilities: project.ourResponsibilities || "",
        customerResponsibilities: project.customerResponsibilities || "",
        outOfScope: project.outOfScope || "",
      });
    }
  }, [isLoading, isFetching]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  //@ts-ignore
  const minimized = configCustomerSummaryState === "minimized";
  const keys = Object.keys(fields);

  const handleSave = async () => {
    const data = {
      id: Number(id),
      type: "projects",
      attributes: {
        "executive-summary": fields.executiveSummary,
        "solution-summary": fields.solutionSummary,
        "our-responsibilities": fields.ourResponsibilities,
        "client-responsibilities": fields.customerResponsibilities,
        "out-of-scope": fields.outOfScope,
      },
    };

    const res = await updateProject({
      id: props.project_id,
      //@ts-ignore
      body: { data },
    });

    //@ts-ignore
    if (res?.data?.data?.id) {
      setResponseMessage("Customer summary updated");
      setShowSuccess(true);
      setIsDirty(false);
    } else {
      handleFailResponse("Something went wrong updating customer summary");
    }
  };

  const handleFailResponse = (msg) => {
    setResponseMessage(msg || "Something went wrong processing this request");
    setShowFail(true);
  };

  const handleGenerateSummary = async () => {
    setShowSummaryRequestAlert(true);
    setGenerating(true);

    const res = await createSummaryRequest({
      projectId: id,
      body: {},
    });

    //@ts-ignore
    const pending = res?.data?.data;
    let status = pending?.attributes?.status || "pending";

    if (pending?.id) {
      if (status === "complete") {
        const updated = {
          ...fields,
          executiveSummary: pending?.attributes?.["executive-summary"] || "",
        };
        setFields(updated);
        setShowSummaryRequestAlert(false);
        setGenerating(false);
        setIsDirty(true);
        return;
      } else {
        while (true) {
          const latest = await store.dispatch(
            generatedV2.endpoints.v2GetExecutiveSummaryRequest.initiate(
              {
                projectId: id,
                id: pending?.id,
              },
              { forceRefetch: true }
            )
          );
          const status = latest?.data?.data?.attributes?.status;

          if (status === "complete" || status === "failed") {
            const executiveSummary =
              latest?.data?.data?.attributes?.["executive-summary"] || "";
            setFields({ ...fields, executiveSummary });
            setShowSummaryRequestAlert(false);
            setGenerating(false);
            setIsDirty(true);
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, 5000));
        }
      }
    } else {
      setGenerating(false);
      setShowSummaryRequestAlert(false);
      handleFailResponse("");
    }
  };

  const building = project?.status === "building";
  const disabled =
    !building ||
    !hasPrivilege("projects.overview", ["create", "manage", "purge"]);

  return (
    <AdminFormPage
      title="Solution Summary and Responsibilites"
      formId={disabled ? undefined : FORM_ID}
    >
      {handleAlert(
        showSuccess,
        responseMessage,
        setShowSuccess,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFail,
        responseMessage,
        setShowFail,
        "warning",
        ToastAlert
      )}
      <Form
        id={FORM_ID}
        className="customerSummaryV2"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className="customerSummaryContent">
          {(minimized ? keys.slice(0, 2) : keys).map((key, i) => (
            <React.Fragment key={key}>
              <Form.Label>{labelFor(key as keyof Fields)}</Form.Label>
              {i === 0 && generating ? (
                <div style={{ minHeight: "147px" }}>
                  <ScopeStackSpinner />
                </div>
              ) : (
                <MarkdownTextAreaV2
                  key={key}
                  value={fields[key]}
                  placeholder={`Enter ${labelFor(
                    key as keyof Fields
                  ).toLowerCase()}`}
                  alwaysEdit={!disabled}
                  disabled={disabled}
                  onChange={(markdown) => {
                    setFields((prev) => ({ ...prev, [key]: markdown }));
                    setIsDirty(true);
                  }}
                  customToolbarButtons={
                    i === 0
                      ? [
                          {
                            name: "generateSummary",
                            icon: (
                              <FontAwesomeIcon icon={faMagicWandSparkles} />
                            ),
                            tooltip:
                              "Generate content based on project details",
                            action: () => handleGenerateSummary(),
                          },
                        ]
                      : []
                  }
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </Form>
      <ScopeStackModal
        modalTitle="Generate Executive Summary"
        modalBody={
          <p>
            Analyzing your project and writing an executive summary. Depending
            on the complexity of your project this may take 30-60 seconds
          </p>
        }
        button1Text="Close"
        handleButton1Click={() => setShowSummaryRequestAlert(false)}
        button2Text={null}
        handleButton2Click={null}
        show={showSummaryRequestAlert}
        handleClose={() => setShowSummaryRequestAlert(false)}
      />
    </AdminFormPage>
  );
};

export default CustomerSummaryV2;
